/**
 * `useVersionGate()` polls /runtime-config.json every 5min (and on
 * `visibilitychange` when the tab becomes visible again) and returns a
 * VersionStatus comparing the embedded build SHA against the server's
 * `client_version` block. See docs/pwa-update-and-offline.md §1.4.
 *
 * Cache-busting strategy: the URL gets `?t=<now>` and `cache: 'no-cache'`
 * is set on the fetch. CloudFront serves the JSON with `no-cache, no-store,
 * must-revalidate`, but defense-in-depth catches misconfigured CDN edges or
 * a buggy intermediate proxy.
 *
 * Resilience: a fetch failure (offline, 5xx) is silently swallowed — we
 * keep the last known status and try again on the next tick. The hook
 * never throws into React's render tree.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { compareStatus, type ClientVersion, type VersionStatus } from './compareStatus'

const POLL_MS = 5 * 60 * 1000 // 5min
const CONFIG_URL = '/runtime-config.json'

async function fetchClientVersion(): Promise<ClientVersion | null> {
  try {
    const url = `${CONFIG_URL}?t=${Date.now()}`
    const res = await fetch(url, { cache: 'no-cache' })
    if (!res.ok) return null
    const json = (await res.json()) as { client_version?: ClientVersion | null }
    return json.client_version ?? null
  } catch {
    // Offline, DNS failure, CORS, parse error — all swallowed. Caller
    // keeps the previous status so a brief blip can't strand the user
    // behind a gate.
    return null
  }
}

interface UseVersionGateResult {
  status: VersionStatus
  /**
   * Triggers the reload action used by the force/soft buttons.
   *
   * Today this is `window.location.reload()`. Once the service worker
   * registration exists, this helper should also call `updateSW(true)`
   * before reloading so the SW activates the new bundle without a second
   * navigation.
   */
  reloadForUpdate: () => void
}

export function useVersionGate(): UseVersionGateResult {
  const [status, setStatus] = useState<VersionStatus>({ kind: 'current' })

  // Tracks the latest in-flight tick so a stale fetch racing past a
  // newer one can't overwrite state.
  const tickRef = useRef(0)

  const check = useCallback(async () => {
    const tick = ++tickRef.current
    const cv = await fetchClientVersion()
    if (tick !== tickRef.current) return
    const next = compareStatus(__APP_VERSION__, cv)
    setStatus((prev) => (sameStatus(prev, next) ? prev : next))
  }, [])

  useEffect(() => {
    // Fire-and-forget: kick off an immediate check + a 5min poll.
    void check()
    const id = window.setInterval(() => {
      void check()
    }, POLL_MS)

    // When the user returns to a tab they left open overnight, we
    // re-check immediately so they don't sit on a stale status for up
    // to 5min.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [check])

  const reloadForUpdate = useCallback(() => {
    window.location.reload()
  }, [])

  return { status, reloadForUpdate }
}

function sameStatus(a: VersionStatus, b: VersionStatus): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'current' || b.kind === 'current') return a.kind === b.kind
  // Both are force_update or soft_update — compare relevant fields.
  return (
    a.current_version === b.current_version &&
    a.target_version === b.target_version &&
    a.message === b.message
  )
}
